import { Controller, type UseFormReturn } from "react-hook-form";
import { type z } from "zod";

import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { useFeatureFlags } from "@hooks/http";
import { CurrencyInput } from "@shared/currency-input";
import { SuffixedInput } from "@shared/suffixed-input";

import { useShowReferencePeriod } from "../../hooks/use-show-reference-period";

import { type ClientSettingSchema } from "./client-setting-form";

type Props = {
  formMeta: UseFormReturn<z.infer<typeof ClientSettingSchema>>;
  isRevenue: boolean;
};

export const SettingEfficiencyGoalsField = ({ formMeta, isRevenue }: Props) => {
  const { user } = useUser();

  const enableMmmReferencePeriod = useShowReferencePeriod();

  return (
    <div className="border-b border-v2-secondary px-6 pt-5 pb-8">
      <label className="block font-semibold">
        {enableMmmReferencePeriod ? "5. " : "4. "} Specify your{" "}
        {isRevenue ? "Profit Margin (%)" : "Profit Margin ($)"} and your{" "}
        {isRevenue ? "Minimum ROAS" : "Maximum CAC"} (optional)
      </label>
      <p className="text-sm mb-5 max-w-[42rem]">
        Enter values below to creates budget mixes that are Profit Maximizing,
        and mixes based on Marketing Efficiency (e.g CAC or ROAS) goals.
      </p>
      <div className="flex items-center gap-6">
        <Controller
          control={formMeta.control}
          name="profit_value_per_unit_kpi"
          render={({ field: { value, onChange } }) =>
            !isRevenue ? (
              <CurrencyInput
                label={
                  <GenericTooltip
                    content={
                      isRevenue ? (
                        <div className="w-[20rem]">
                          E.g If you are making 80% profit margin on your
                          orders, then you should enter &apos;80%&apos;.
                          <div className="mt-1" />
                          This means you are making 80 cents for every dollar of
                          revenue you are getting.
                        </div>
                      ) : (
                        <div className="w-[20rem]">
                          E.g If you sell 100 orders, and your avg profit across
                          those 100 orders is $90, then you should enter
                          &apos;$90&apos;.
                        </div>
                      )
                    }
                  >
                    {isRevenue
                      ? "OVERALL PROFIT MARGIN (%)"
                      : "AVG PROFIT MARGIN ACROSS ORDERS ($)"}
                  </GenericTooltip>
                }
                className="flex-1"
                inputClassName="text-right"
                errorClassName="static mt-1"
                errorMessage={
                  formMeta.formState.errors.profit_value_per_unit_kpi?.message
                }
                min={0}
                code={user.currencies.currentCurrency}
                onChange={onChange}
                value={value}
                decimal
              />
            ) : (
              <SuffixedInput
                label={
                  <GenericTooltip
                    content={
                      isRevenue ? (
                        <div className="w-[20rem]">
                          E.g If you are making 80% profit margin on your
                          orders, then you should enter &apos;80%&apos;.
                          <div className="mt-1" />
                          This means you are making 80 cents for every dollar of
                          revenue you are getting.
                        </div>
                      ) : (
                        <div className="w-[20rem]">
                          E.g If you sell 100 orders, and your avg profit across
                          those 100 orders is $90, then you should enter
                          &apos;$90&apos;.
                        </div>
                      )
                    }
                  >
                    {isRevenue
                      ? "OVERALL PROFIT MARGIN (%)"
                      : "AVG PROFIT MARGIN ACROSS ORDERS ($)"}
                  </GenericTooltip>
                }
                className="flex-1"
                inputClassName="text-right"
                errorClassName="static mt-1"
                errorMessage={
                  formMeta.formState.errors.profit_value_per_unit_kpi?.message
                }
                max={100}
                min={0}
                suffix="%"
                onChange={onChange}
                value={value}
              />
            )
          }
        />
        <Controller
          control={formMeta.control}
          name="max_cost_per_unit_kpi"
          render={({ field: { value, onChange } }) =>
            !isRevenue ? (
              <CurrencyInput
                label={
                  <GenericTooltip
                    content={
                      <>
                        Enter the {isRevenue ? "minimum ROAS" : "maximum CAC"}{" "}
                        you want to optimize for.
                      </>
                    }
                  >
                    {isRevenue ? "MINIMUM ROAS" : "MAXIMUM CAC"}
                  </GenericTooltip>
                }
                className="flex-1"
                inputClassName="text-right"
                errorClassName="static mt-1"
                errorMessage={
                  formMeta.formState.errors.max_cost_per_unit_kpi?.message
                }
                min={0}
                code={user.currencies.currentCurrency}
                onChange={onChange}
                value={value}
                decimal
              />
            ) : (
              <SuffixedInput
                label={
                  <GenericTooltip
                    content={
                      <>
                        Enter the {isRevenue ? "minimum ROAS" : "maximum CAC"}{" "}
                        you want to optimize for.
                      </>
                    }
                  >
                    {isRevenue ? "MINIMUM ROAS" : "MAXIMUM CAC"}
                  </GenericTooltip>
                }
                className="flex-1"
                inputClassName="text-right"
                errorClassName="static mt-1"
                errorMessage={
                  formMeta.formState.errors.max_cost_per_unit_kpi?.message
                }
                min={0}
                suffix="×"
                onChange={onChange}
                decimal
                value={value}
              />
            )
          }
        />
      </div>
    </div>
  );
};
